/**
 * useLiveOverlayState - Live overlay copy, embed chrome, and host sync
 *
 * Keeps the derived live overlay text and host `postMessage` payloads out of
 * App.tsx while preserving the existing embedded/live UI behavior.
 */

import { MutableRefObject, useEffect, useMemo } from 'react';

import type { AvatarSessionStatus } from '../../session';

export interface LivePromptOption {
  label: string;
  value: string;
}

export interface UseLiveOverlayStateOptions {
  avatarLoadingMessage?: string | null;
  isEmbeddedBuilderView: boolean;
  isEmbeddedView: boolean;
  /**
   * True for the runtime host's Visual IVR route (/visual). The phone
   * has the call and the browser is visual-only — the user didn't ask
   * to see alternate avatars and shouldn't be able to switch them,
   * because the project has a specific avatar assigned. Hides the
   * Previous/Next avatar switcher chrome regardless of embed state.
   */
  isVisualOnly: boolean;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  liveAssistantText: string;
  liveChatEventId: number;
  liveChatText: string;
  liveConnectionError: string | null;
  liveControlCaption: string;
  liveModeActive: boolean;
  livePrimaryButtonText: string;
  livePrimaryControlBusy: boolean;
  livePromptFieldCount: number;
  livePromptOptions: LivePromptOption[];
  livePromptTextRef: MutableRefObject<string>;
  liveSessionStatus?: AvatarSessionStatus;
  liveTranscriptEventId: number;
  liveUserTranscript: string;
  liveUserTranscriptFinal: boolean;
  liveVoiceSessionActive: boolean;
  suppressEmbeddedLiveCopyOverride?: boolean;
}

export interface UseLiveOverlayStateReturn {
  hideEmbeddedBuilderLiveChrome: boolean;
  hideEmbeddedLiveAvatarSwitcher: boolean;
  liveEmbedCaption: string;
  liveEmbedPromptText: string;
  liveInlineAssistantText: string;
  liveStatusText: string;
  shouldShowVoiceOverlay: boolean;
  suppressEmbeddedLiveCopy: boolean;
}

export function useLiveOverlayState({
  avatarLoadingMessage,
  isEmbeddedBuilderView,
  isEmbeddedView,
  isVisualOnly,
  isMobileCall,
  isWebrtcCall,
  liveAssistantText,
  liveChatEventId,
  liveChatText,
  liveConnectionError,
  liveControlCaption,
  liveModeActive,
  livePrimaryButtonText,
  livePrimaryControlBusy,
  livePromptFieldCount,
  livePromptOptions,
  livePromptTextRef,
  liveSessionStatus,
  liveTranscriptEventId,
  liveUserTranscript,
  liveUserTranscriptFinal,
  liveVoiceSessionActive,
  suppressEmbeddedLiveCopyOverride
}: UseLiveOverlayStateOptions): UseLiveOverlayStateReturn {
  const liveStatusText = useMemo(() => {
    if (!liveModeActive) {
      return '';
    }

    if (liveConnectionError) {
      return liveConnectionError;
    }

    switch (liveSessionStatus) {
      case 'connecting':
        return avatarLoadingMessage || 'Connecting to your call...';
      case 'thinking':
        return 'Thinking...';
      case 'ended':
        return 'Live session ended';
      case 'error':
        return liveConnectionError || 'Live session error';
      default:
        return '';
    }
  }, [avatarLoadingMessage, liveConnectionError, liveModeActive, liveSessionStatus]);

  const liveInlineAssistantText = useMemo(() => {
    if (!liveModeActive) {
      return '';
    }

    return liveAssistantText || livePromptTextRef.current || liveStatusText || '';
  }, [liveAssistantText, liveModeActive, livePromptTextRef, liveStatusText]);

  const suppressEmbeddedLiveCopy = useMemo(() => (
    liveModeActive && (isEmbeddedView || suppressEmbeddedLiveCopyOverride === true)
  ), [isEmbeddedView, liveModeActive, suppressEmbeddedLiveCopyOverride]);

  const liveEmbedCaption = suppressEmbeddedLiveCopy ? '' : liveControlCaption;
  const liveEmbedPromptText = suppressEmbeddedLiveCopy ? '' : liveInlineAssistantText;
  const hideEmbeddedBuilderLiveChrome = liveModeActive && isEmbeddedBuilderView;
  // Switcher hidden in three situations:
  //   1. Embedded live mode (the host app has its own avatar-selection UI)
  //   2. Visual IVR — the project picked a specific avatar; users shouldn't
  //      be able to change it mid-call
  const hideEmbeddedLiveAvatarSwitcher = (liveModeActive && isEmbeddedView) || isVisualOnly;

  useEffect(() => {
    if (window.parent === window) {
      return;
    }

    window.parent.postMessage({
      type: 'avatar.live.state',
      payload: {
        status: liveSessionStatus ?? (liveVoiceSessionActive ? 'active' : 'idle'),
        caption: liveEmbedCaption,
        primaryActionLabel: livePrimaryButtonText,
        primaryActionDisabled: livePrimaryControlBusy,
        promptText: liveEmbedPromptText,
        chatText: liveChatText || liveInlineAssistantText,
        chatEventId: liveChatEventId,
        promptOptions: livePromptOptions,
        transcriptText: liveUserTranscript,
        transcriptFinal: liveUserTranscriptFinal,
        transcriptEventId: liveTranscriptEventId,
        connectionError: liveConnectionError ?? undefined
      }
    }, '*');
  }, [
    liveChatEventId,
    liveChatText,
    liveConnectionError,
    liveEmbedCaption,
    liveEmbedPromptText,
    liveInlineAssistantText,
    livePrimaryButtonText,
    livePrimaryControlBusy,
    livePromptOptions,
    liveSessionStatus,
    liveTranscriptEventId,
    liveUserTranscript,
    liveUserTranscriptFinal,
    liveVoiceSessionActive
  ]);

  const shouldShowVoiceOverlay = useMemo(() => {
    if (liveModeActive) {
      return Boolean(
        livePromptOptions.length > 0 ||
        livePromptFieldCount > 0 ||
        liveConnectionError ||
        (!suppressEmbeddedLiveCopy && liveUserTranscript) ||
        (!suppressEmbeddedLiveCopy && (liveInlineAssistantText || liveStatusText))
      );
    }

    return isWebrtcCall || isMobileCall;
  }, [
    isMobileCall,
    isWebrtcCall,
    liveConnectionError,
    liveInlineAssistantText,
    liveModeActive,
    livePromptFieldCount,
    livePromptOptions.length,
    liveStatusText,
    liveUserTranscript,
    suppressEmbeddedLiveCopy
  ]);

  return {
    hideEmbeddedBuilderLiveChrome,
    hideEmbeddedLiveAvatarSwitcher,
    liveEmbedCaption,
    liveEmbedPromptText,
    liveInlineAssistantText,
    liveStatusText,
    shouldShowVoiceOverlay,
    suppressEmbeddedLiveCopy
  };
}

export default useLiveOverlayState;
